// Grouped review entries: several review items that ask one question render
// as one entry the reviewer decides in one action (ReviewItemGroupDrawer).
// Each member stays its own item on the wire, so every write, apply and
// lineage rule is the item's own; grouping is how the list reads.
//
//   - teams: the team names an award or modification states (Workstream
//     identity items), confirmed together, each editable or rejectable;
//   - same: one conflict a document repeats line by line (the same field,
//     reason and choices on several lines), settled with one value.
import { displayValue, fieldLabel, isOpenReviewItem, picksTeam } from './reviewItemCopy';
// A team-naming item: the stated name of a team the award or a modification
// funds (pangaea's award and modification naming).
export function isTeamNaming(r) {
    return picksTeam(r) && r.targetObjectType === 'Workstream';
}
function sameKey(r) {
    if (r.candidates.length < 2) return '';
    const labels = r.candidates.map((c)=>c.label || c.key);
    return [
        r.targetObjectType,
        r.targetFieldPath,
        String(r.reasonCode),
        ...labels
    ].join('\u0001');
}
// groupReviewItems keeps the list's order: a group sits where its first
// member was. A would-be group of one stays a single item.
export function groupReviewItems(items) {
    const keyOf = (d)=>{
        if (isTeamNaming(d.review)) return 'teams';
        const k = sameKey(d.review);
        return k ? `same:${k}` : '';
    };
    const byKey = new Map();
    for (const d of items){
        const k = keyOf(d);
        if (!k) continue;
        byKey.set(k, [
            ...byKey.get(k) ?? [],
            d
        ]);
    }
    const out = [];
    const placed = new Set();
    for (const d of items){
        const k = keyOf(d);
        const members = k ? byKey.get(k) ?? [] : [];
        if (members.length < 2) {
            out.push({
                kind: 'single',
                item: d
            });
            continue;
        }
        if (placed.has(k)) continue;
        placed.add(k);
        out.push({
            kind: 'group',
            group: {
                id: k,
                mode: k === 'teams' ? 'teams' : 'same',
                members
            }
        });
    }
    return out;
}
export function isOpenEntry(e) {
    return e.kind === 'single' ? isOpenReviewItem(e.item) : e.group.members.some(isOpenReviewItem);
}
// The name a team item states: its new-team candidate's label.
export function statedTeamName(r) {
    const c = r.candidates.find((x)=>!x.workstreamId) ?? r.candidates[0];
    return c?.label ?? '';
}
// The line a member is on, for a same-value group's list.
export function memberLine(r) {
    return r.targetLocator.trim();
}
// A group's title is short: the question, not the explanation (the drawer
// body carries the reason). Two choices read "<field>: A or B?"; more read
// "<field> on N lines: which value?".
export function groupTitle(g) {
    const n = g.members.length;
    if (g.mode === 'teams') return `Confirm these ${n} teams`;
    const r = g.members[0].review;
    const field = fieldLabel(r.targetFieldPath);
    if (r.candidates.length === 2) {
        const [a, b] = r.candidates.map((c)=>displayValue(c.valueJson, r.targetFieldPath) || c.label);
        return `${field}: ${a} or ${b}? (${n} lines)`;
    }
    return `${field} on ${n} lines: which value?`;
}
export function groupSummary(g) {
    const names = g.mode === 'teams' ? g.members.map((m)=>statedTeamName(m.review)) : g.members.map((m)=>memberLine(m.review));
    const shown = names.slice(0, 6).join(', ');
    return names.length > 6 ? `${shown} and ${names.length - 6} more` : shown;
}
